/**
 * Dashboard / Jobs "reuse last document" routing.
 * Maps legacy document_type values to canonical types and wizard URLs.
 */

import { buildCoverLetterGeneratePath, isCoverLetterDocumentType } from './coverLetterRoutes';

const DOCUMENT_TYPE_REUSE_ALIASES = {
  business_proposal: 'business_grant_proposal',
  grant_proposal: 'business_grant_proposal',
  business_plan: 'business_plan_funding',
  business_plan_funding: 'business_plan_funding',
  business_grant_proposal: 'business_grant_proposal',
  ngo_grant_proposal: 'ngo_grant_proposal',
  service_proposal: 'service_proposal',
};

const REUSE_WIZARD_ROUTES = {
  business_plan_funding: '/proposals/business-plan',
  business_plan: '/proposals/business-plan',
  business_grant_proposal: '/documents/business-proposal/generate',
  business_proposal: '/documents/business-proposal/generate',
  ngo_grant_proposal: '/documents/ngo-grant-proposal/generate',
  service_proposal: '/documents/service-proposal/generate',
};

export function normalizeDocumentTypeForReuse(documentType) {
  const key = (documentType || '').trim();
  return DOCUMENT_TYPE_REUSE_ALIASES[key] || key;
}

/**
 * Resolve the wizard route for a document type (legacy or canonical).
 * @param {string} documentType
 * @returns {string|null}
 */
export function getReuseRoute(documentType) {
  const normalized = normalizeDocumentTypeForReuse(documentType);
  return REUSE_WIZARD_ROUTES[normalized] || null;
}

/**
 * Build the generate/reuse URL for a prior job.
 * @param {string} documentType - raw document_type from job or /documents/last
 * @param {string} jobId
 * @param {{ templateId?: string, templateName?: string }} [opts]
 */
export function buildReuseGenerateUrl(documentType, jobId, opts = {}) {
  const normalized = normalizeDocumentTypeForReuse(documentType);

  if (isCoverLetterDocumentType(normalized)) {
    return buildCoverLetterGeneratePath({
      rerunJobId: jobId,
      ...(opts.templateId ? { templateId: opts.templateId } : {}),
      ...(opts.templateName ? { templateName: opts.templateName } : {}),
    });
  }

  const base = getReuseRoute(normalized)
    || `/documents/${normalized.replace(/_/g, '-')}/generate`;

  const params = new URLSearchParams();
  if (jobId) params.set('rerunJobId', jobId);
  if (opts.templateId) params.set('templateId', opts.templateId);
  if (opts.templateName) params.set('templateName', opts.templateName);

  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}
